import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Col, 
  Form, 
  Row, 
  Table, 
  Spinner, 
  Pagination, 
  Badge,
  InputGroup,
  Modal,
  Card
} from 'react-bootstrap';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { ArrowLeft, FileEarmarkArrowUp, Printer, Search } from 'react-bootstrap-icons';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';

const CentralStTransaction = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const navigate = useNavigate();
    
    const accessToken = localStorage.getItem('authToken');
    const [sortedBy, setSortedBy] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [rmMaincategoryList, setRmMaincategoryList] = useState([]);
    const [selectedRMCategory, setSelectedRMCategory] = useState('');
    const [rawMaterialList, setRawMaterialList] = useState([]);
    const [subCategoryList, setSubCategoryList] = useState([]);
    const [subCategoryId, setSubCategoryId] = useState('');
    const [selectedRawMaterials, setSelectedRawMaterials] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(50);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [clicked, setClicked] = useState(false);
    const [stockReportData, setStockReportData] = useState([]);
    const [error, setError] = useState('');
    const [searchText, setSearchText] = useState('');
    const [selectedTransactionType, setSelectedTransactionType] = useState('');
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('pdf');
    const [storageLocations, setStorageLocations] = useState([]);
    const [selectedStorageLocation, setSelectedStorageLocation] = useState('');
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    const options = rmMaincategoryList?.map((rmcat) => ({
        value: rmcat?._id,
        label: rmcat?.name,
    }));

    const subCategoryOptions = [
        { value: 'all', label: 'All Sub Categories' },
        ...subCategoryList?.map((rm) => ({
            value: rm._id,
            label: rm.name
        }))
    ];

    const rawMaterialOptions = rawMaterialList?.map((rm) => ({
        value: rm?._id,
        label: rm?.name,
    }));

    // Storage location options
    const storageLocationOptions = storageLocations?.map(location => ({
        value: location?._id,
        label: location?.name,
    }));

    const handleStorageLocationChange = (selectedOption) => {
        setSelectedStorageLocation(selectedOption?.value || '');
    };

    const getStorageLocations = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("storage-location", { headers });

            if (response?.data?.status === true) {
                const activeLocations = response?.data?.data?.storageLocations;
                setStorageLocations(activeLocations);
            }
        } catch (error) {
            console.log("Storage Location Error: ", error);
            
            // handleApiError(error);
        }
    };

    const transactionTypeOptions = [
        { value: '', label: 'All Transaction Types' },
        { value: 'PURCHASE', label: 'Purchase' },
        { value: 'STOCK-TRANSFER', label: 'Stock Transfer' },
        { value: 'OUTLET-RETURN', label: 'Outlet Return' },
        { value: 'DAMAGE', label: 'Damage' },
        { value: 'ADJUSTMENT', label: 'Adjustment' }
    ];

    useEffect(() => {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
        const end = new Date(currentDate.setHours(23, 59, 59, 999));
        
        setStartDate(start);
        setEndDate(end);
        getRawMaterialsMainCategory();
        getRawMaterials();
        getStorageLocations();
    }, []);

    useEffect(() => {
        if (clicked && searchText === "") {
            const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
            const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
            getStockTransactionReport(currentPage, recordsPerPage, sortedBy, selectedRMCategory, selectedRawMaterials, subCategoryId, selectedTransactionType, adjustedStartDate, adjustedEndDate, selectedStorageLocation);
        }
    }, [recordsPerPage, currentPage]);

    const getRawMaterialsMainCategory = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("masters/rm-main-category/get", {
                headers,
            });

            if (response?.data?.status === true) {
                setRmMaincategoryList(response?.data?.data?.categories);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };

    const getSubCategoryRawMaterials = async (mainId) => {
        try {
          const headers = {
            Authorization: `Bearer ${accessToken}`,
          };
          const response = await axios.get("masters/rm-sub-category/get", {
            headers,
          });
    
          if (response?.data?.status === true) {
            let filteredData = response?.data?.data?.categories?.filter((sub) => sub?.mainCategory === mainId);
            setSubCategoryList(filteredData);
          }
        } catch (error) {
          if (error.response && error.response?.data?.status === false) {
            let errorMessage = error.response.data.error;
            toast.error(errorMessage, {
              position: "top-right",
              autoClose: 3000,
              theme: "colored",
            });
          }
        }
    };

    const getRawMaterials = async (categoryId, subCategoryId) => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };

            const response = await axios.get(`raw-material`, { headers });

            if (response?.data?.status === true) {
                let filteredRawmaterials
                if (subCategoryId === undefined) {
                    filteredRawmaterials = response?.data?.data?.rawMaterials?.filter((fi) => fi.mainCategory._id === categoryId);
                } else {
                    filteredRawmaterials = response?.data?.data?.rawMaterials?.filter((fi) => fi.mainCategory._id === categoryId && fi?.subCategory?._id === subCategoryId);                    
                }
            
                if (categoryId === undefined && subCategoryId === undefined) {
                    setRawMaterialList(response?.data?.data?.rawMaterials);
                } else {
                    setRawMaterialList(filteredRawmaterials);                    
                }
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };

    const handleDownloadExcel = async() => {
        setDownloadBtnLoading(true);
        const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
        console.log("end Date :", endDate);
        const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
        getStockTransactionReport(currentPage, recordsPerPage, sortedBy, selectedRMCategory, selectedRawMaterials, subCategoryId, selectedTransactionType, adjustedStartDate, adjustedEndDate, selectedStorageLocation, true);
    }

    const getStockTransactionReport = async(page, limit, sortBy, rmMainCategory, rawMaterial, rmSubCategory, transaction, start, end, storage, download) => {
        setLoading(true);
        console.log("Hello :");
        
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            }
            let url = `stock-transactions/getCentralStockTransactions?fromDate=${start.toISOString().split("T")[0]}&toDate=${end.toISOString().split("T")[0]}&page=${page}&limit=${limit}`
            
            if (sortBy) {
                url += `&sort=${sortBy}`;
            }

            if (storage) {
                url += `&storageLocation=${storage}`;
            }

            if (rmMainCategory) {
                url += `&mainCategory=${rmMainCategory}`;
            }

            if (rawMaterial) {
                url += `&rawMaterialId=${rawMaterial}`;
            }

            if (rmSubCategory) {
                url += `&subCategory=${rmSubCategory}`;
            }

            if (transaction) {
                url += `&transactionType=${transaction}`;
            }

            if (download) {
                url +=  `&isDownload=${download}`
            }

            const response = await axios.get(url, 
                { 
                    headers, 
                    responseType: download ? 'blob' : 'json'
                }
            );

            if (download) {
                // Create a blob from the response data
                const blob = new Blob([response.data], { 
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                });
                
                // Create a link element
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                
                // Set the filename for the download
                const fileName = `Central_Stock_Transactions.xlsx`;
                link.download = fileName;
                
                // Append the link to the body and trigger the download
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
        
                toast.success('Excel file downloaded successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light"
                });
            } else {
                setStockReportData(response?.data?.data?.centralStockTransactionRecords);
                setTotalPages(response?.data?.data?.paginationData?.NumberOfPages || 1);
            }

        } catch (error) {
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) {
                    errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.error;
                } else if (error.response?.status === 412) {
                    errorMessage = error?.response?.data?.data?.message;
                } else if (error.response?.status === 500) {
                    errorMessage = error?.response?.data?.error;
                }

                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            }
        } finally {
            setLoading(false);
            setDownloadBtnLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setClicked(true);
        setCurrentPage(1);
        
        const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
        const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
        
        await getStockTransactionReport(1, recordsPerPage, sortedBy, selectedRMCategory, selectedRawMaterials, subCategoryId, selectedTransactionType, adjustedStartDate, adjustedEndDate, selectedStorageLocation);
        
        toast.success("Report generated successfully", {
            position: "top-right",
            autoClose: 2000,
            theme: "colored",
        });
    }

    const handlePrintReport = () => {
        const formatDate = (date) => {
            return new Date(date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        };
        
        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);

        const tableContent = `
            <table style="border-collapse: collapse; width: 100%; font-size: 12px;">
                <thead>
                    <tr style="background-color: #f8f9fa;">
                        <th style="border: 1px solid #dee2e6; padding: 8px;">#</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">Date & Time</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">Raw Materials Details</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">Storage location</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">Type</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Quantity</th>
                    </tr>
                </thead>
                <tbody>
                    ${stockReportData?.length > 0 ? 
                        stockReportData?.map((record, index) => `
                            <tr>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">${index + 1}</td>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">
                                    ${new Date(record?.createdAt).toLocaleDateString('en-GB')}<br>
                                    <small>${new Date(record?.createdAt).toLocaleTimeString('en-GB')}</small>
                                </td>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">
                                    <strong>${record?.rawMaterial?.name}</strong><br>
                                    <small>${record?.rawMaterial?.mainCategory?.name} > ${record?.rawMaterial?.subCategory?.name}</small><br>
                                    <small>${record?.remark || 'No remarks'}</small>
                                </td>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">
                                    ${record?.referenceNumber || 'N/A'}<br>
                                    ${record?.storageLocation ? `<small>${record?.storageLocation?.name}</small>` : ''}
                                </td>
                                <td style="border: 1px solid #dee2e6; padding: 8px; color: ${record?.type === "CREDIT" ? "green" : "red"}">
                                    ${record?.type}<br>
                                    <small>(${record?.transactionType})</small>
                                </td>
                                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                                    ${record?.quantity?.toFixed(2)} ${record?.rawMaterial?.unit}
                                </td>
                            </tr>
                        `).join("") :
                        `<tr>
                            <td colspan="6" style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">No data available</td>
                        </tr>`
                    }
                </tbody>
            </table>
        `;
    
        const win = window.open("", "_blank");
        win.document.write(`
            <html>
                <head>
                    <title>Central Stock Transaction</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
                        .title { font-size: 18px; font-weight: bold; }
                        .date-range { font-size: 14px; color: #666; }
                        table { width: 100%; border-collapse: collapse; }
                        th { background-color: #f8f9fa; text-align: left; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div class="title">Central Stock Transaction</div>
                        <div class="date-range">${formattedStartDate} to ${formattedEndDate}</div>
                    </div>
                    ${tableContent}
                    <script>
                        setTimeout(() => {
                            window.print();
                            window.close();
                        }, 500);
                    </script>
                </body>
            </html>
        `);
        win.document.close();
    }

    const handleEndDateChange = (date) => {
        if (date && startDate && date < startDate) {
            setError('End date cannot be earlier than start date');
        } else {
            setError('');
            setEndDate(date);
        }
    };

    const renderPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;
        let startPage, endPage;

        if (totalPages <= maxVisiblePages) {
            startPage = 1;
            endPage = totalPages;
        } else {
            const maxVisibleBeforeCurrent = Math.floor(maxVisiblePages / 2);
            const maxVisibleAfterCurrent = Math.ceil(maxVisiblePages / 2) - 1;
            
            if (currentPage <= maxVisibleBeforeCurrent) {
                startPage = 1;
                endPage = maxVisiblePages;
            } else if (currentPage + maxVisibleAfterCurrent >= totalPages) {
                startPage = totalPages - maxVisiblePages + 1;
                endPage = totalPages;
            } else {
                startPage = currentPage - maxVisibleBeforeCurrent;
                endPage = currentPage + maxVisibleAfterCurrent;
            }
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item 
                    key={i} 
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item 
                    key={totalPages} 
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
            const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
            const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
            getStockTransactionReport(page, recordsPerPage, sortedBy, selectedRMCategory, selectedRawMaterials, subCategoryId, selectedTransactionType, adjustedStartDate, adjustedEndDate, selectedStorageLocation);
        }
    };

    const handleRMCategoryChange = (selectedOption) => {
        const categoryId = selectedOption ? selectedOption.value : '';
        setSelectedRMCategory(categoryId);
        if (categoryId) {
            getRawMaterials(categoryId);
            getSubCategoryRawMaterials(categoryId);
        } else {
            setRawMaterialList([]);
            setSubCategoryList([]);
        }
    };

    const handleSubCategoryChange = (selectedOption) => {
        const subCategoryId = selectedOption ? selectedOption.value : '';
        setSubCategoryId(subCategoryId);
        if (subCategoryId) {
            getRawMaterials(selectedRMCategory, subCategoryId);
        } else {
            setRawMaterialList([]);
            setSubCategoryList([]);
        }    
    };

    const handleTransactionTypeChange = (selectedOption) => {
        setSelectedTransactionType(selectedOption ? selectedOption.value : '');
    }

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-0 fw-bold d-inline-block">
                                <i className="bi bi-arrow-left-right me-2"></i>CENTRAL STOCK TRANSACTIONS
                            </h4>
                        </div>
                        <div>
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={() => navigate('/reports/all-reports')}
                                className="me-3 d-flex align-items-center fw-semibold"
                                style={{ height: '38px' }}
                            >
                                <ArrowLeft className="me-1" /> Back to Reports
                            </Button>
                        </div>
                    </Card.Header>
                    <hr className='mt-0 mb-0' /> 
                    
                    <Card.Body>
                        {/* Filters Section */}
                        <div className="p-3 bg-light rounded-3">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={3}>
                                        <Form.Group controlId="storageLocationSelect">
                                            <Form.Label className="fw-semibold">
                                                Storage Location
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                options={storageLocationOptions}
                                                value={storageLocationOptions?.find(option => option.value === selectedStorageLocation) || null}
                                                onChange={handleStorageLocationChange}
                                                placeholder="Select Storage Location"
                                                className="border-secondary"
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        borderColor: '#6c757d',
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    {/* <Col md={2}>
                                        <Form.Group controlId="startDate">
                                            <Form.Label className="fw-semibold">
                                                Start Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Start Date"
                                                className="form-control border-secondary"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="endDate">
                                            <Form.Label className="fw-semibold">
                                                End Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={handleEndDateChange}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="End Date"
                                                className="form-control border-secondary"
                                                minDate={startDate}
                                            />
                                            {error && <div className="text-danger small mt-1">{error}</div>}
                                        </Form.Group>
                                    </Col> */}

                                    <Col md={2}>
                                        <Form.Group controlId="startDate">
                                            <Form.Label className="fw-semibold">
                                            Start Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => {
                                                    setStartDate(date);
                                                    // Reset endDate if it's outside the new 60-day window
                                                    if (endDate && date && endDate > new Date(date.getTime() + 60 * 24 * 60 * 60 * 1000)) {
                                                    setEndDate(null);
                                                    }
                                                }}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Start Date"
                                                className="form-control border-secondary"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="endDate">
                                            <Form.Label className="fw-semibold">
                                                End Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => {
                                                    if (startDate && date > new Date(startDate.getTime() + 60 * 24 * 60 * 60 * 1000)) {
                                                    setError("End date cannot be more than 60 days after start date");
                                                    } else {
                                                    setError("");
                                                    handleEndDateChange(date);
                                                    }
                                                }}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="End Date"
                                                className="form-control border-secondary"
                                                minDate={startDate}
                                                maxDate={startDate ? new Date(startDate.getTime() + 60 * 24 * 60 * 60 * 1000) : null}
                                            />
                                            {error && <div className="text-danger small mt-1">{error}</div>}
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="sortBy">
                                            <Form.Label className="fw-semibold">
                                                Sort By
                                            </Form.Label>
                                            <Form.Select
                                                value={sortedBy}
                                                onChange={(e) => setSortedBy(e.target.value)}
                                                className="border-secondary"
                                            >
                                                <option value="">Descending</option>
                                                <option value="ASC">Ascending</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="rmCategory">
                                            <Form.Label className="fw-semibold">
                                                Main RM Category
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                value={options.find(option => option.value === selectedRMCategory)}
                                                onChange={handleRMCategoryChange}
                                                options={options}
                                                placeholder="Select Main RM Categories"
                                                className="border-secondary"
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        borderColor: '#6c757d',
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    
                                </Row>

                                <Row className="g-3 mt-2">
                                    <Col md={3}>
                                        <Form.Group controlId="subCategory">
                                            <Form.Label className="fw-semibold">
                                                RM Sub Category
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                isSearchable
                                                placeholder="RM Sub Category"
                                                options={subCategoryOptions}
                                                isDisabled={!selectedRMCategory}
                                                value={subCategoryOptions.find(option => option.value === subCategoryId) || null}
                                                onChange={handleSubCategoryChange}
                                                className="border-secondary"
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        borderColor: '#6c757d',
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group controlId="rawMaterial">
                                            <Form.Label className="fw-semibold">
                                                Raw Material
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                value={rawMaterialOptions.find(option => option.value === selectedRawMaterials)}
                                                onChange={(selectedOption) => {
                                                    setSelectedRawMaterials(selectedOption ? selectedOption.value : '');
                                                }}
                                                options={rawMaterialOptions}
                                                placeholder="Select Raw Material"
                                                className="border-secondary"
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        borderColor: '#6c757d',
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="transactionType">
                                            <Form.Label className="fw-semibold">
                                                Transaction Type
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                options={transactionTypeOptions}
                                                value={transactionTypeOptions.find(option => option.value === selectedTransactionType)}
                                                onChange={handleTransactionTypeChange}
                                                placeholder="Select Transaction Type"
                                                className="border-secondary"
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        borderColor: '#6c757d',
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="w-100"
                                            disabled={!startDate || !endDate}
                                        >
                                            {loading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>

                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="outline-success"
                                            className="w-100"
                                            onClick={handleDownloadExcel}
                                        >
                                            <FileEarmarkArrowUp className="me-2" />
                                            { downloadBtnLoading ? <Spinner size='sm' /> : 'Export Report'}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </div>

                        {/* Search and Actions Section */}
                        {/* {clicked && (
                            <div className="mb-4 p-3 bg-light rounded-3">
                                <Row className="g-3 align-items-center">
                                    <Col md={6}>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by material name or reference"
                                                value={searchText}
                                                onChange={(e) => setSearchText(e.target.value)}
                                                className="border-secondary"
                                            />
                                            <Button 
                                                variant="outline-secondary"
                                                className='w-25'
                                                onClick={handleSubmit}
                                            >
                                                <Search /> Search
                                            </Button>
                                        </InputGroup>
                                    </Col>
                                </Row>
                            </div>
                        )} */}

                        {/* Results Section */}
                        {!clicked ? (
                            <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
                                <div className="text-center">
                                    <i className="bi bi-clipboard-data fs-1 text-muted"></i>
                                    <h5 className="text-muted mt-3">
                                        Please select date range and filters to view transactions
                                    </h5>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Pagination and Records Per Page - Top */}
                                <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                                    <div className="d-flex align-items-center">
                                        <span className="me-2 text-muted">Show</span>
                                        <Form.Select
                                            style={{ width: 'auto' }}
                                            value={recordsPerPage}
                                            onChange={(e) => {
                                                const selectedRecordsPerPage = Number(e.target.value);
                                                setRecordsPerPage(selectedRecordsPerPage);
                                                setCurrentPage(1);
                                                const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
                                                const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
                                                getStockTransactionReport(1, selectedRecordsPerPage, sortedBy, selectedRMCategory, selectedRawMaterials, subCategoryId, selectedTransactionType, adjustedStartDate, adjustedEndDate, selectedStorageLocation);
                                            }}
                                            className="border-secondary"
                                        >
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                            <option value={300}>300</option>
                                            <option value={500}>500</option>
                                        </Form.Select>
                                        <span className="ms-2 text-muted">entries</span>
                                    </div>
                                    
                                    <Pagination className="mb-0">
                                        <Pagination.Prev
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        />
                                        {renderPaginationItems()}
                                        <Pagination.Next
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        />
                                    </Pagination>
                                </div>

                                {/* Stock Transactions Table */}
                                <div className="table-responsive border rounded-3">
                                    <Table striped bordered hover className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th width="50">#</th>
                                                <th width="120">Date & Time</th>
                                                <th>Raw Materials Details</th>
                                                <th>Storage location</th>
                                                <th width="120">Type</th>
                                                <th width="100" className="text-end">Quantity</th>
                                                {/* <th width="100" className="text-end">Balance</th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">
                                                        <Spinner animation="border" variant="primary" />
                                                        <p className="mt-2 mb-0">Loading transactions...</p>
                                                    </td>
                                                </tr>
                                            ) : stockReportData?.length > 0 ? (
                                                stockReportData.map((transaction, index) => (
                                                    <tr key={index}>
                                                        <td className="text-muted">
                                                            {(currentPage - 1) * recordsPerPage + index + 1}
                                                        </td>
                                                        <td>
                                                            <small>{new Date(transaction?.createdAt).toLocaleDateString('en-GB')}</small><br />
                                                            <small className="text-muted">
                                                                {new Date(transaction?.createdAt).toLocaleTimeString('en-GB')}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold">{transaction?.rawMaterial?.name}</div>
                                                            <small className="text-primary">
                                                                {transaction?.rawMaterial?.mainCategory?.name} &gt; {transaction?.rawMaterial?.subCategory?.name}
                                                            </small><br />
                                                            <small className="text-muted">{transaction?.remark || 'No remarks'}</small>
                                                        </td>
                                                        <td>
                                                            {transaction?.storageLocation?.name || 'N/A'}<br />
                                                            {transaction?.storageLocation && (
                                                                <small>
                                                                    {transaction?.storageLocation?.city}, {transaction?.storageLocation?.state}
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td style={{ color: transaction?.type === "CREDIT" ? "green" : "red" }}>
                                                            {transaction?.type}<br />
                                                            <small>({transaction?.transactionType})</small>
                                                        </td>
                                                        <td className="text-end">
                                                            {transaction?.quantity?.toFixed(2)} {transaction?.rawMaterial?.unit}
                                                        </td>
                                                        {/* <td className="text-end">
                                                            {transaction?.currentStock?.toFixed(2)} {transaction?.rawMaterial?.unit}
                                                        </td> */}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">
                                                        <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                                                        <p className="mt-2 mb-0 text-muted">
                                                            No transactions found for the selected criteria
                                                        </p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Pagination and Records Per Page - Bottom */}
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted">
                                        Showing {(currentPage - 1) * recordsPerPage + 1} to{' '}
                                        {Math.min(currentPage * recordsPerPage, stockReportData?.length)} of{' '}
                                        {stockReportData?.length} entries
                                    </div>
                                    <Pagination>
                                        <Pagination.Prev
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        />
                                        {renderPaginationItems()}
                                        <Pagination.Next
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        />
                                    </Pagination>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>

                {/* Export Modal */}
                <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Export Report</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Select Export Format</Form.Label>
                            <Form.Select 
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value)}
                            >
                                <option value="pdf">PDF</option>
                                <option value="excel">Excel</option>
                                <option value="csv">CSV</option>
                            </Form.Select>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowExportModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={() => {
                            // Handle export logic here
                            setShowExportModal(false);
                        }}>
                            Export
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </React.Fragment>
    );
};

export default CentralStTransaction;